import React, { useState, useEffect } from "react";
import TableCompo from "../Table/TableCompo";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import {
  getDepositRequest,
  deleteDepositRequest,
  updateDepositStatusRequest,
} from "../../../redux/BullionDeposit/bullionDepositSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getMetalsRequest } from "../../../redux/GetMetals/metalSlice";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
} from "@mui/material";
import useCsvConverter from "../../../hooks/convertToCSV";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import { canShowPermission } from "../Permissions/permissionObject";

const BullionDepositList = () => {
  const { data = {} } = useSelector((state) => state.metal || {});
  const enabledMetals = data.metal || [];

  const dispatch = useDispatch();
  const {
    plans = [],
    total = 0,
    loading,
  } = useSelector((state) => state.bullionDeposit);

  const [page, setPage] = useState(0); // MUI uses zero-based
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({ page: 0, limit: 5 });


  useEffect(() => {
    dispatch(
      getDepositRequest({
        page: pagination.page + 1,
        limit: pagination.limit,
        search: searchText,
      }),
    );
  }, [dispatch, pagination, searchText]);

  const handleTableChange = (action, tableState) => {
    if (action === "changePage") {
      setPagination((prev) => ({ ...prev, page: tableState.page }));
    } else if (action === "changeRowsPerPage") {
      setPagination({ page: 0, limit: tableState.rowsPerPage });
    } else if (action === "search") {
      setSearchText(tableState.searchText || "");
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  //  Confirm Status Dialog
  const [confirmStatusDialogOpen, setConfirmStatusDialogOpen] = useState(false);
  const [selectedStatusPlanId, setSelectedStatusPlanId] = useState(null);

  const handleConfirmStatusChange = (id) => {
    setSelectedStatusPlanId(id);
    setConfirmStatusDialogOpen(true);
  };

  const handleConfirmStatusYes = () => {
    dispatch(updateDepositStatusRequest(selectedStatusPlanId));
    setConfirmStatusDialogOpen(false);
    setSelectedStatusPlanId(null);
  };

  const handleConfirmStatusNo = () => {
    setConfirmStatusDialogOpen(false);
    setSelectedStatusPlanId(null);
  };

  // Confirm Delete Dialog
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [selectedDeletePlanId, setSelectedDeletePlanId] = useState(null);

  const handleDeleteClick = (id) => {
    setSelectedDeletePlanId(id);
    setConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDeleteYes = () => {
    dispatch(deleteDepositRequest(selectedDeletePlanId));
    dispatch(getDepositRequest());
    toast.success("Deposit plan deleted");
    setConfirmDeleteDialogOpen(false);
    setSelectedDeletePlanId(null);
  };

  const handleConfirmDeleteNo = () => {
    setConfirmDeleteDialogOpen(false);
    setSelectedDeletePlanId(null);
  };

  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
      }
    }
  }, []);

  const canEdit = canShowPermission(permissions, "createDepositPlan", "edit");
  const canDelete = canShowPermission(
    permissions,
    "createDepositPlan",
    "delete",
  );
  const columns = [
    {
      field: "postdate",
      headerName: "Creation Date",
      render: (_, row) =>
        new Date(row.createdAt).toLocaleDateString("en-IN") || "-",
    },
    { field: "name", headerName: "Name" },
    { field: "qty", headerName: "Qty" },
    {
      field: "metal",
      headerName: "Metal",
      render: (v, row) =>
        row.bullion
          ? `${row.bullion.metalName} ${row.bullion.karat}K - ${row.bullion.purity}`
          : "N/A",
    },
    {
      field: "interest",
      headerName: "Benefit/Annum",
      render: (v, row) => (row.interest !== null ? `${row.interest}` : "-"),
    },
    {
      field: "sendby",
      headerName: "Status",
      render: (value, row) => {
        const isEnabled = row.enabled === true;

        if (!canEdit) {
          return (
            <Box sx={{ textAlign: "center", width: "100%" }}>
              <span
                style={{
                  fontWeight: 600,
                  color: isEnabled ? "#17a2b8" : "#e53935",
                }}
              >
                {isEnabled ? "Enable" : "Disable"}
              </span>
            </Box>
          );
        }

        return (
          <button
            onClick={() => handleConfirmStatusChange(row._id)}
            style={{
              backgroundColor: isEnabled ? "#17a2b8" : "#e53935",
              color: "#fff",
              border: "none",
              padding: "5px 10px",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {isEnabled ? "Enable" : "Disable"}
          </button>
        );
      },
    },
    ...(canDelete
      ? [
          {
            field: "action",
            headerName: "Action",
            render: (value, row) => (
              <RiDeleteBin6Line
                style={{ cursor: "pointer", color: "gray" }}
                onClick={() => handleDeleteClick(row._id)}
              />
            ),
          },
        ]
      : []),
    {
      field: "createdBY",
      headerName: "Created By",
      width: 200,
      hideInExcel: true,
      render: (value, row) => {
        return row?.createdBy ? row.createdBy.name : "N/A";
      },
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      width: 200,
      hideInExcel: true,
      render: (value, row) => {
        return row?.updatedBy ? row.updatedBy.name : "N/A";
      },
    },
  ];

  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    console.log("Store CSV API called");

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/bullion-deposit/bullionDepositCsv",
        query: {},
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "bullionDeposit.csv");
        },
      }),
    );
  };

  return (
    <>
      <TableCompo
        title="Bullion Deposit List"
        columns={columns}
        data={JSON.parse(JSON.stringify(plans)).reverse()}
        totalCount={total}
        onTableChange={handleTableChange}
        loading={loading}
        page={pagination.page}
        limit={pagination.limit}
        extraButtons={
          <Button size="sm" variant="outlined" onClick={handleDownloadStoreCsv}>
            Download CSV
          </Button>
        }
      />

      {/*  Status Change Dialog */}
      <Dialog open={confirmStatusDialogOpen} onClose={handleConfirmStatusNo}>
        <DialogTitle>Change Deposit Plan Status</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to change the status of this deposit plan?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }}
          >
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirmStatusYes}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmStatusNo}
            >
              No
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/*  Delete Dialog */}
      <Dialog open={confirmDeleteDialogOpen} onClose={handleConfirmDeleteNo}>
        <DialogTitle>Delete Deposit Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this deposit plan?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }}
          >
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteYes}
            >
              Yes, Delete
            </Button>
            <Button variant="contained" onClick={handleConfirmDeleteNo}>
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BullionDepositList;
